import React from 'react';
import { useGetStorefrontQuery } from '../../Api/Api';
import QuoteRequestDrawer from '../Cart/QuoteRequestDrawer';
import { useLocalization } from '../Helpers/useLocalization';

interface IQuotePriceProps {
    // The quote-only item this placeholder stands in for.
    itemId?: string;
    itemName?: string;
    itemType?: 'product' | 'bundle';
    quantity?: number;
    // Rendered element, so each page keeps the heading/label markup it already had.
    as?: 'h2' | 'span' | 'div';
    className?: string;
    style?: React.CSSProperties;
}

/**
 * The "Request a Quote" text shown in place of the price on hide-price items. When the storefront allows
 * quote requests (and has a sales rep to route them to) the text becomes a button that opens the same
 * quote panel the cart uses, pre-filled with just this item. Otherwise it stays plain text.
 */
const QuotePrice: React.FC<IQuotePriceProps> = (props) => {
    const L = useLocalization();
    const settings = useGetStorefrontQuery();
    const [open, setOpen] = React.useState(false);
    const Element = props.as ?? 'span';

    // Same gate as the cart's Request a Quote button: the flow mails the request to the default sales rep,
    // so without one there is nowhere for it to go.
    const clickable = !!settings.data?.allowRequestQuote && !!settings.data?.defaultSalesRep && !!props.itemId;

    const label = L('localization_quote_price_label');

    if (!clickable) {
        return <Element className={props.className} style={props.style}>{label}</Element>;
    }

    return (
        <>
            <Element
                className={`${props.className ?? ''} quote-price-clickable`.trim()}
                style={props.style}
                role='button'
                tabIndex={0}
                title={label}
                // Cards and search rows navigate on click — the quote panel must not also open the product.
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                }}
            >
                {label}
            </Element>
            <QuoteRequestDrawer
                open={open}
                onOpenChange={setOpen}
                subject={props.itemName}
                items={[{
                    id: props.itemId!,
                    quantity: props.quantity && props.quantity > 0 ? props.quantity : 1,
                    itemType: props.itemType ?? 'product',
                }]}
            />
        </>
    );
};

export default QuotePrice;
